import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Send, Menu, X, ChevronRight } from 'lucide-react';
import { useLang } from '../context/LanguageContext';

const LOGO = '/img/logo.png';

export default function Header() {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navLinks = [
    { label: t.nav.about, href: '#about' },
    { label: t.nav.format, href: '#format' },
    { label: t.nav.finances, href: '#finances' },
    { label: t.nav.investments, href: '#investments' },
    { label: t.nav.gallery, href: '#gallery' },
    { label: t.nav.contacts, href: '#contacts' },
  ];

  const handleNav = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'py-3 bg-[#0A0B0F]/95 backdrop-blur-2xl border-b border-white/[0.08] shadow-2xl' : 'py-5 bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="flex-shrink-0 transition-transform duration-300 hover:scale-105">
          <img src={LOGO} alt="KidsMafia" className="h-11 w-auto object-contain" />
        </button>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => handleNav(link.href)}
              className="nav-link"
              data-testid={`nav-${link.href.replace('#','')}`}
            >
              {link.label}
            </button>
          ))}
        </nav>

        {/* Desktop Actions */}
        <div className="hidden lg:flex items-center gap-4">
          {/* Language Switcher - Enhanced */}
          <div data-testid="language-switcher" className="flex items-center gap-1 mr-2 glass-premium rounded-full px-1.5 py-1.5">
            <button
              data-testid="lang-btn-ru"
              onClick={() => setLang('ru')}
              className={`px-4 py-2 rounded-full text-xs font-black transition-all duration-300 ${
                lang === 'ru' ? 'bg-[#D4AF37] text-[#0A0B0F] shadow-lg' : 'text-[#BFC3C9] hover:text-white'
              }`}
            >
              RU
            </button>
            <button
              data-testid="lang-btn-es"
              onClick={() => setLang('es')}
              className={`px-4 py-2 rounded-full text-xs font-black transition-all duration-300 ${
                lang === 'es' ? 'bg-[#D4AF37] text-[#0A0B0F] shadow-lg' : 'text-[#BFC3C9] hover:text-white'
              }`}
            >
              ES
            </button>
          </div>

          <a
            href="https://t.me/SerjValencia"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-telegram-btn"
            className="w-11 h-11 rounded-full glass-premium flex items-center justify-center text-[#229ED9] hover:bg-[#229ED9]/20 transition-all duration-300 hover:scale-110"
          >
            <Send size={18} />
          </a>

          <a
            href="tel:+34624586917"
            data-testid="header-call-btn"
            className="btn-call flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold"
          >
            <Phone size={16} />
            {t.nav.call}
          </a>
        </div>

        {/* Mobile Controls */}
        <div className="flex lg:hidden items-center gap-3">
          <div data-testid="language-switcher-mobile" className="flex items-center gap-1 glass rounded-full px-1 py-1">
            <button
              onClick={() => setLang('ru')}
              className={`px-2.5 py-1 rounded-full text-xs font-bold transition-all ${
                lang === 'ru' ? 'bg-[#D4AF37] text-black' : 'text-[#BFC3C9]'
              }`}
            >RU</button>
            <button
              onClick={() => setLang('es')}
              className={`px-2.5 py-1 rounded-full text-xs font-bold transition-all ${
                lang === 'es' ? 'bg-[#D4AF37] text-black' : 'text-[#BFC3C9]'
              }`}
            >ES</button>
          </div>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="w-10 h-10 glass rounded-full flex items-center justify-center text-white"
          >
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="lg:hidden overflow-hidden bg-[#12141A]/95 backdrop-blur-xl border-t border-white/[0.06]"
          >
            <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-2">
              {navLinks.map((link, i) => (
                <motion.button
                  key={link.href}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  onClick={() => handleNav(link.href)}
                  className="flex items-center justify-between py-3 border-b border-white/[0.06] text-[#BFC3C9] hover:text-white transition-colors text-left"
                >
                  <span className="font-medium">{link.label}</span>
                  <ChevronRight size={16} className="text-[#D4AF37]" />
                </motion.button>
              ))}
              <div className="flex gap-3 mt-4">
                <a href="tel:+34624586917" className="btn-call flex-1 flex items-center justify-center gap-2 py-3 rounded-full text-sm">
                  <Phone size={15} /> {t.nav.call}
                </a>
                <a href="https://t.me/SerjValencia" target="_blank" rel="noopener noreferrer"
                  className="btn-telegram flex-1 flex items-center justify-center gap-2 py-3 rounded-full text-sm">
                  <Send size={15} /> Telegram
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
